export const CASE_VALUE_THREAD_MAX_PROVIDER_CALLS = 8;
export const CASE_VALUE_THREAD_MAX_CANDIDATES = 8;
export const CASE_VALUE_THREAD_DEFAULT_CANDIDATES = 5;
export const CASE_VALUE_THREAD_MAX_BRIDGE_WINDOW_HOURS = 72;

export const CASE_VALUE_THREAD_HEADERS = Object.freeze([
  'Path Label',
  'Transaction Hash',
  'From Address',
  'To Address',
  'UTC Date',
  'Amount',
  'TokenSymbol',
  '',
  '',
  'System Notes',
  'To Address Attribution',
  'Analyst Notes',
  'Funds Followed',
  'Prior Balance',
  'From Hash',
  '',
  'Continues As',
  'Left Bounds Display',
  'Left Bounds Numeric',
  'Chain Id',
  'Chain Name',
  'Hop Logic',
  'Case Buddy / Tool 88 Notes',
  'From Address Attribution',
]);

function cleanScalar(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export function caseValueThreadExpandScientific(value) {
  const input = cleanScalar(value).replace(/,/g, '');
  if (!input) return '';
  const match = input.match(/^([+-]?)(\d*)(?:\.(\d*))?[eE]([+-]?\d+)$/u);
  if (!match) return input;

  const sign = match[1] === '-' ? '-' : '';
  const integer = match[2] || '0';
  const fraction = match[3] || '';
  const exponent = Number(match[4]);
  if (!Number.isInteger(exponent) || Math.abs(exponent) > 100000) return input;

  const digits = `${integer}${fraction}` || '0';
  const decimalIndex = integer.length + exponent;
  let expanded;
  if (decimalIndex <= 0) {
    expanded = `0.${'0'.repeat(-decimalIndex)}${digits}`;
  } else if (decimalIndex >= digits.length) {
    expanded = `${digits}${'0'.repeat(decimalIndex - digits.length)}`;
  } else {
    expanded = `${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
  }
  return `${sign}${expanded}`;
}

export function caseValueThreadNormalizeDecimal(value) {
  let input = caseValueThreadExpandScientific(value).replace(/,/g, '').trim();
  if (!input) return '';
  const match = input.match(/^([+-]?)(\d*)(?:\.(\d*))?$/u);
  if (!match) return '';
  const sign = match[1] === '-' ? '-' : '';
  let integer = match[2] || '0';
  let fraction = match[3] || '';
  integer = integer.replace(/^0+(?=\d)/u, '') || '0';
  fraction = fraction.replace(/0+$/u, '');
  const zero = /^0+$/u.test(integer) && (!fraction || /^0+$/u.test(fraction));
  if (zero) return '0';
  return `${sign}${integer}${fraction ? `.${fraction}` : ''}`;
}

export function caseValueThreadDecimalFromRaw(rawValue, decimals) {
  const raw = cleanScalar(rawValue).replace(/,/g, '');
  const decimalPlaces = Number(cleanScalar(decimals));
  if (!/^[+-]?\d+$/u.test(raw) || !Number.isInteger(decimalPlaces) || decimalPlaces < 0 || decimalPlaces > 100000) return '';
  const negative = raw.startsWith('-');
  let digits = raw.replace(/^[+-]/u, '').replace(/^0+(?=\d)/u, '') || '0';
  if (decimalPlaces === 0) return caseValueThreadNormalizeDecimal(`${negative ? '-' : ''}${digits}`);
  digits = digits.padStart(decimalPlaces + 1, '0');
  const split = digits.length - decimalPlaces;
  return caseValueThreadNormalizeDecimal(`${negative ? '-' : ''}${digits.slice(0, split)}.${digits.slice(split)}`);
}

export function caseValueThreadEconomicClue(amount, token, fundsFollowed) {
  const funds = cleanScalar(fundsFollowed);
  const fundsMatch = funds.match(/([+-]?(?:\d+(?:,\d{3})*|\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)\s+([^\s]+)\s*$/u);
  if (fundsMatch) {
    const normalized = caseValueThreadNormalizeDecimal(fundsMatch[1]);
    if (normalized) {
      const fundsToken = cleanScalar(fundsMatch[2]);
      return {
        amount: normalized,
        token: fundsToken,
        source: 'funds_followed',
        display: `${normalized}${fundsToken ? ` ${fundsToken}` : ''}`,
      };
    }
  }
  const normalizedAmount = caseValueThreadNormalizeDecimal(amount);
  const normalizedToken = cleanScalar(token);
  return {
    amount: normalizedAmount,
    token: normalizedToken,
    source: 'transaction_amount',
    display: [normalizedAmount, normalizedToken].filter(Boolean).join(' '),
  };
}

export function caseValueThreadBoundOptions(options = {}) {
  const windowRaw = Number(options.bridgeWindowHours);
  const maxRaw = Number(options.maxCandidates);
  return {
    analyzeSwaps: options.analyzeSwaps !== false,
    analyzeBridges: options.analyzeBridges !== false,
    analyzeWrapUnwrap: options.analyzeWrapUnwrap !== false,
    bridgeWindowHours: Math.max(1, Math.min(CASE_VALUE_THREAD_MAX_BRIDGE_WINDOW_HOURS, Number.isFinite(windowRaw) ? Math.round(windowRaw) : 24)),
    preferCache: options.preferCache !== false,
    forceRefresh: options.forceRefresh === true,
    maxCandidates: Math.max(1, Math.min(CASE_VALUE_THREAD_MAX_CANDIDATES, Number.isFinite(maxRaw) ? Math.round(maxRaw) : CASE_VALUE_THREAD_DEFAULT_CANDIDATES)),
  };
}

export function caseValueThreadClone(value) {
  if (typeof structuredClone === 'function') {
    try { return structuredClone(value); } catch { /* use recursive clone */ }
  }
  if (Array.isArray(value)) return value.map(caseValueThreadClone);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, caseValueThreadClone(nested)]));
  }
  return value;
}

export function caseValueThreadFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.values(value).forEach(caseValueThreadFreeze);
  return Object.freeze(value);
}

function fnv1a(input) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function caseValueThreadRowSignature(row = []) {
  const values = Array.from({ length: 24 }, (_, index) => cleanScalar(row?.[index]));
  return `vt-row-v1:${fnv1a(JSON.stringify(values))}`;
}

export function caseValueThreadCandidateEvent(candidate = {}, side = 'continuation') {
  const direction = cleanScalar(candidate?.direction || candidate?.anchorDirection).toLowerCase();
  if (side === 'earlier') return candidate?.earlierEvent || candidate?.sourceEvent || null;
  if (side === 'later') return candidate?.laterEvent || candidate?.destinationEvent || null;
  return direction === 'reverse'
    ? (candidate?.earlierEvent || candidate?.sourceEvent || null)
    : (candidate?.laterEvent || candidate?.destinationEvent || null);
}

export function caseValueThreadCandidateSummary(candidate = {}, requestId = '') {
  const earlier = candidate?.earlierEvent || candidate?.sourceEvent || {};
  const later = candidate?.laterEvent || candidate?.destinationEvent || {};
  const line = event => [
    event.chain || event.chainName,
    event.transactionHash,
    [event.exactQuantityString || event.decimalAmountString || event.displayAmount, event.tokenSymbol || event.tokenId].filter(Boolean).join(' '),
  ].filter(Boolean).join(' · ');
  const evidence = Array.isArray(candidate?.evidenceMatches) ? candidate.evidenceMatches : [];
  const conflicts = Array.isArray(candidate?.evidenceConflicts) ? candidate.evidenceConflicts : [];
  return [
    `${candidate.status || 'Candidate'} · ranking score ${Number(candidate.score) || 0}/100 (not probability)`,
    candidate.kind ? `Type: ${candidate.kind}` : '',
    line(earlier) ? `Earlier: ${line(earlier)}` : '',
    line(later) ? `Later: ${line(later)}` : '',
    evidence.length ? `Supporting evidence: ${evidence.join('; ')}` : '',
    conflicts.length ? `Conflicts: ${conflicts.join('; ')}` : '',
    requestId ? `Request: ${requestId}` : '',
  ].filter(Boolean).join('\n');
}
function scrubSensitive(value, depth = 0) {
  if (depth > 8) return '[truncated]';

  if (Array.isArray(value)) {
    return value
      .slice(0, 50)
      .map(item => scrubSensitive(item, depth + 1));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((memo, [key, nested]) => {
      if (/authorization|bearer|api[-_ ]?key|token|secret|password|credential/iu.test(key)) {
        return memo;
      }

      memo[key] = scrubSensitive(nested, depth + 1);
      return memo;
    }, {});
  }

  const text = value;

  if (typeof text === 'string') {
    return text
      .replace(/Bearer\s+[A-Za-z0-9._~+/-]+/giu, '[redacted]')
      .replace(
        /(?:api[-_ ]?key|authorization|secret|password)\s*[:=]\s*[^\s,;]+/giu,
        '[redacted]'
      )
      .slice(0, 4000);
  }

  return text;
}

export function caseValueThreadSanitizedDiagnostics(result = {}) {
  const stages = Array.isArray(result?.search?.stages)
    ? result.search.stages.map(stage => ({
      stage: stage.stage || stage.name || '',
      status: stage.status || '',
      httpStatus: stage.httpStatus ?? stage.http_code ?? null,
      entitlement: stage.entitlement || '',
      providerCalled: stage.providerCalled === true,
      cacheHit: stage.cacheHit === true,
      resultCount: stage.resultCount ?? null,
      warning: stage.warning || '',
    }))
    : [];
  const safe = {
    requestId: result.requestId || result.request_id || '',
    serviceVersion: result.serviceVersion || '',
    status: result.status || '',
    search: {
      providerCallCount: result?.search?.providerCallCount ?? 0,
      maximumProviderCalls: result?.search?.maximumProviderCalls ?? CASE_VALUE_THREAD_MAX_PROVIDER_CALLS,
      searchComplete: result?.search?.searchComplete !== false,
      truncationReason: result?.search?.truncationReason || '',
      cacheHits: result?.search?.cacheHits ?? 0,
      cacheMisses: result?.search?.cacheMisses ?? 0,
      usage: scrubSensitive(result?.search?.usage || {}),
      stages,
    },
    candidateCount: Array.isArray(result.candidates) ? result.candidates.length : 0,
    warnings: Array.isArray(result.warnings) ? result.warnings.slice(0, 30) : [],
  };
  return JSON.stringify(scrubSensitive(safe), null, 2);
}

export function caseValueThreadShort(value, start = 10, end = 8) {
  const text = cleanScalar(value);
  if (!text || text.length <= start + end + 1) return text;
  return `${text.slice(0, start)}…${text.slice(-end)}`;
}
